import React, { useState, useEffect } from 'react';
import { useFormik } from "formik";
import * as Yup from "yup";
import { IoClose } from 'react-icons/io5';
import { MdFastfood } from "react-icons/md";
import { CImage } from '@coreui/react';
import ImageUploading from 'react-images-uploading';
import AsyncSelect from 'react-select/async';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

import { useTranslation } from 'react-i18next';
import { getCategorias, getItemPrivate, updateItens } from "../../services/api";
import { getStoredUser } from '../../utils/safeStorage';
import InputMoney from "../../pages/ListItem/InputMoney";
import '../../styles/modal-shared.css';

/**
 * ModalEditItem - Modal para editar itens/tamanhos do cardapio
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla se o modal esta aberto
 * @param {function} props.onClose - Funcao chamada ao fechar o modal
 * @param {function} props.onSuccess - Funcao chamada apos salvar com sucesso
 * @param {string} props.itemObjId - ID do item a ser editado
 * @param {number} props.modelo - Modelo do item (1 = simples, 2 = pizza)
 */
const ModalEditItem = ({
  isOpen,
  onClose,
  onSuccess,
  itemObjId,
  modelo
}) => {
  const { t } = useTranslation('categorias');
  const user = getStoredUser();
  const userID = user?._id;

  const empresa = localStorage.getItem('empresa');
  const empresaParse = empresa ? JSON.parse(empresa) : {};
  const idEmpresa = empresaParse.id_empresa;
  const objIdEmpresa = empresaParse._id;
  const vinculo_empresa = empresaParse.cnpj;

  const [item, setItem] = useState({});
  const [valuePreco, setValuePreco] = useState(0);
  const [valueDescricao, setValueDescricao] = useState('');
  const [images, setImages] = useState([]);
  const [imagesToString, setImagesToString] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const INITIAL_DATA = {
    value: "",
    label: t('editItemModal.categoryPlaceholder'),
  };
  const [selectData, setselectData] = useState(INITIAL_DATA);

  const maxNumber = 1;
  const maxMbFileSize = 2 * 1024 * 1024; // 2Mb

  const mapResponseToValuesAndLabels = (data) => ({
    value: data._id,
    label: data.title,
  });

  async function callApi(value) {
    const data = await getCategorias(userID, idEmpresa, vinculo_empresa, objIdEmpresa)
      .then((response) => response.data.categorias)
      .then((response) => {
        if ((modelo?.value === 2 || modelo === 2)) {
          return response.filter((categoria) => (categoria.modelo?.value === 2 || categoria.modelo === 2));
        } else if ((modelo?.value === 1 || modelo === 1) || !modelo) {
          return response.filter((categoria) => (categoria.modelo?.value === 1 || categoria.modelo === 1) || !categoria.modelo);
        }
        return response;
      })
      .then((response) => response.sort((a, b) => a.order - b.order))
      .then((response) => response.map(mapResponseToValuesAndLabels))
      .then((response) => response.filter((i) => i.label.toLowerCase().includes(value?.toLowerCase() || '')));

    return data;
  }

  const onChangeImg = (imageList, addUpdateIndex) => {
    setImages(imageList);
    const newImagesToString = imageList.length > 0 ? imageList.map(image => image.data_url).join(", ") : "";
    setImagesToString(newImagesToString);
  };

  useEffect(() => {
    if (isOpen && itemObjId) {
      setLoading(true);
      const loadItem = async () => {
        try {
          const response = await getItemPrivate(itemObjId);
          const itemData = response.data.item;
          setItem(itemData);

          if (itemData) {
            setselectData({
              value: itemData.category_item_id,
              label: itemData.category_item_title,
            });
            setValueDescricao(itemData.description || '');
            setValuePreco(itemData.price || 0);
            setImages(itemData.image ? [{ data_url: itemData.image }] : []);
            setImagesToString(itemData.image || '');

            formik.setValues({
              vinculo_categoria: itemData.category_item_id,
              title: itemData.title,
              preco: itemData.price,
              type: itemData.type,
            });
          }
        } catch (error) {
          console.error("Erro ao carregar item:", error);
          toast(t('editItemModal.loadingError'), { autoClose: 3000, type: "error" });
        } finally {
          setLoading(false);
        }
      };
      loadItem();
    }
  }, [isOpen, itemObjId]);

  const SignUpSchema = Yup.object().shape({
    title: Yup.string().required(t('actions.required')),
    vinculo_categoria: !selectData.value && Yup.string().required(t('actions.required')),
    type: Yup.string().required(t('actions.required')),
  });

  const formik = useFormik({
    validationSchema: SignUpSchema,
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      vinculo_categoria: "",
      title: "",
      preco: 0,
      type: "",
    },
    onSubmit: (values) => {
      const description = valueDescricao;
      const title = values.title;
      const price = values.preco;
      const type = values.type;
      handleSubmitAPI(title, description, price, type);
    },
  });

  const handleSubmitAPI = (title, description, price, type) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const createdBy = userID;
    const out = false;
    const category_item_id = selectData.value;
    const category_item_title = selectData.label;
    const id_item = item.id_item;
    const has_adicional = item.has_adicional || false;
    const adicionais = item.adicionais || [];
    const orderItem = item.order || 0;

    const imagesToSend = images.length > 0 ? images : (imagesToString || null);

    updateItens(createdBy, itemObjId, id_item, idEmpresa, orderItem, category_item_id, category_item_title, title, description, out, imagesToSend, price, has_adicional, adicionais, type)
      .then(cast => {
        if (cast.status === 200) {
          toast(cast.data.msg, { autoClose: 3000, type: "success" });
          if (onSuccess) {
            onSuccess();
          }
          onClose();
        } else {
          toast(cast.data.msg, { autoClose: 3000, type: "error" });
        }
      })
      .catch(err => {
        const msg = err.response?.data?.msg || t('item.updateError');
        toast(msg, { autoClose: 3000, type: "error" });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleMaxCharDescricao = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= 1000) {
      setValueDescricao(newValue);
    }
  };

  const onChange = (event, nameObject, isNumber) => {
    setValuePreco(isNumber ? Number(event.target.value) : event.target.value);
    formik.setFieldValue("preco", isNumber ? Number(event.target.value) : event.target.value);
  };

  const isPizza = modelo?.value === 2 || modelo === 2;

  // Estilos do AsyncSelect alinhados ao design system
  const asyncSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '40px',
      border: state.isFocused ? '1.5px solid #318CD5' : '1.5px solid #e2e8f0',
      borderRadius: '10px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(49, 140, 213, 0.12)' : 'none',
      fontSize: '14px',
      '&:hover': { borderColor: state.isFocused ? '#318CD5' : '#cbd5e1' }
    }),
    input: (base) => ({ ...base, color: '#1e293b', fontSize: '14px' }),
    singleValue: (base) => ({ ...base, color: '#1e293b', fontSize: '14px' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '14px' }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? '#318CD5' : isFocused ? '#eff6ff' : '#fff',
      color: isSelected ? '#fff' : '#1e293b',
      fontSize: '14px',
      cursor: 'pointer',
    }),
    menu: (base) => ({ ...base, borderRadius: '10px', overflow: 'hidden', zIndex: 10 }),
    menuList: (base) => ({ ...base, padding: '4px 0' }),
  };

  return (
    <div className={isOpen ? 'pdj-modal-overlay' : 'pdj-modal-overlay--hidden'}>
      <div className="pdj-modal-box">

        {loading ? (
          <div className="pdj-modal-spinner" />
        ) : (
          <form onSubmit={formik.handleSubmit}>

            {/* Header */}
            <div className="pdj-modal-header">
              <div className="pdj-modal-header-text">
                <h2 className="pdj-modal-title">
                  {isPizza ? t('editItemModal.titlePizza') : t('editItemModal.title')}
                </h2>
                <p className="pdj-modal-subtitle">
                  {isPizza ? t('editItemModal.subtitlePizza') : t('editItemModal.subtitle')}
                </p>
              </div>
              <button type="button" className="pdj-modal-close-btn" onClick={onClose}>
                <IoClose />
              </button>
            </div>

            {/* Body */}
            <div className="pdj-modal-body">

              {/* Tipo + Categoria em grid */}
              <div className="pdj-modal-grid-1-2">
                {/* Tipo */}
                <div className="pdj-modal-field">
                  <label className="pdj-modal-label">
                    {t('editItemModal.type')}
                    <span className="pdj-modal-required">*</span>
                  </label>
                  <select
                    className="pdj-modal-select"
                    id="type"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                  >
                    <option value="">{t('editItemModal.typePlaceholder')}</option>
                    <option value="simples">{t('editItemModal.typeSimple')}</option>
                    <option value="pizza">{t('editItemModal.typePizza')}</option>
                  </select>
                  {formik.errors.type && (
                    <span className="pdj-modal-error">{formik.errors.type}</span>
                  )}
                </div>

                {/* Categoria */}
                <div className="pdj-modal-field">
                  <label className="pdj-modal-label">
                    {t('editItemModal.category')}
                    <span className="pdj-modal-required">*</span>
                  </label>
                  <AsyncSelect
                    key={`cat-select-${modelo?.value || modelo || 'default'}`}
                    loadOptions={callApi}
                    onChange={(data) => setselectData(data)}
                    value={selectData}
                    defaultOptions
                    id="vinculo_categoria"
                    name="vinculo_categoria"
                    placeholder={t('editItemModal.categoryPlaceholder')}
                    styles={asyncSelectStyles}
                  />
                  {formik.errors.vinculo_categoria && (
                    <span className="pdj-modal-error">{formik.errors.vinculo_categoria}</span>
                  )}
                </div>
              </div>

              {/* Nome */}
              <div className="pdj-modal-field">
                <label className="pdj-modal-label">
                  {t('editItemModal.itemName')}
                  <span className="pdj-modal-required">*</span>
                </label>
                <input
                  className="pdj-modal-input"
                  placeholder={isPizza ? t('editItemModal.itemNamePlaceholderPizza') : t('editItemModal.itemNamePlaceholder')}
                  id="title"
                  type="text"
                  name="title"
                  onChange={formik.handleChange}
                  value={formik.values.title}
                />
                {formik.errors.title && (
                  <span className="pdj-modal-error">{formik.errors.title}</span>
                )}
              </div>

              {/* Descricao */}
              <div className="pdj-modal-field">
                <label className="pdj-modal-label">
                  {t('editItemModal.description')}
                </label>
                <textarea
                  className="pdj-modal-textarea"
                  placeholder={t('editItemModal.descriptionPlaceholder')}
                  value={valueDescricao}
                  onChange={handleMaxCharDescricao}
                  maxLength="1000"
                />
                <span className="pdj-modal-char-count">
                  {t('editItemModal.descriptionCount', { count: valueDescricao.length })}
                </span>
              </div>

              {/* Preco (apenas se nao for pizza) */}
              {!isPizza && (
                <div className="pdj-modal-field" style={{ maxWidth: '240px' }}>
                  <label className="pdj-modal-label">
                    {t('editItemModal.price')}
                  </label>
                  <InputMoney
                    onChange={(event) => onChange(event, 'price', true)}
                    value={valuePreco}
                    className="pdj-modal-input"
                    placeholder="0,00"
                  />
                </div>
              )}

              {/* Imagem */}
              <div className="pdj-modal-field">
                <label className="pdj-modal-label">
                  {t('editItemModal.image')}
                </label>
                <ImageUploading
                  multiple
                  value={images}
                  onChange={onChangeImg}
                  maxNumber={maxNumber}
                  maxFileSize={maxMbFileSize}
                  acceptType={["jpg", "jpeg", "png", "webp"]}
                  dataURLKey="data_url"
                >
                  {({
                    imageList,
                    onImageUpload,
                    onImageUpdate,
                    onImageRemove,
                    isDragging,
                    dragProps,
                    errors
                  }) => (
                    <div>
                      <div
                        onClick={images.length === 0 ? onImageUpload : undefined}
                        {...dragProps}
                        className={`pdj-modal-upload${isDragging ? ' pdj-modal-upload--dragging' : ''}${imageList.length > 0 ? ' pdj-modal-upload--has-image' : ''}`}
                      >
                        {imageList.length === 0 && (
                          <>
                            <MdFastfood className="pdj-modal-upload-icon" />
                            <div className="pdj-modal-upload-title">
                              {t('editItemModal.imageChoose')}
                            </div>
                            <div className="pdj-modal-upload-hint">
                              {isDragging ? "Solte aqui..." : t('editItemModal.imageDragDrop')}
                            </div>
                          </>
                        )}
                        {imageList.map((image, index) => (
                          <div key={index} className="pdj-modal-upload-preview">
                            <CImage
                              rounded
                              thumbnail
                              fluid
                              src={image['data_url']}
                              style={{ maxHeight: '140px', maxWidth: '100%', borderRadius: '8px' }}
                            />
                            <div className="pdj-modal-upload-actions">
                              <button
                                type="button"
                                className="pdj-modal-btn pdj-modal-btn--secondary"
                                onClick={(e) => { e.stopPropagation(); onImageUpdate(index); }}
                              >
                                {t('addItemModal.imageChange')}
                              </button>
                              <button
                                type="button"
                                className="pdj-modal-btn pdj-modal-btn--danger"
                                onClick={(e) => { e.stopPropagation(); onImageRemove(index); }}
                              >
                                {t('addItemModal.imageRemove')}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {errors && (
                        <div style={{ marginTop: '4px' }}>
                          {errors.maxNumber && <span className="pdj-modal-error">{t('addItemModal.imageMaxNumber')}</span>}
                          {errors.acceptType && <span className="pdj-modal-error">{t('addItemModal.imageInvalidFormat')}</span>}
                          {errors.maxFileSize && <span className="pdj-modal-error">{t('addItemModal.imageMaxFileSize')}</span>}
                        </div>
                      )}

                      <div className="pdj-modal-upload-info">
                        {t('editItemModal.imageMaxSize')}
                      </div>
                    </div>
                  )}
                </ImageUploading>
              </div>

            </div>

            {/* Footer */}
            <div className="pdj-modal-footer">
              <button
                type="button"
                className="pdj-modal-btn pdj-modal-btn--secondary"
                onClick={onClose}
              >
                {t('actions.cancel')}
              </button>
              <button
                type="submit"
                className="pdj-modal-btn pdj-modal-btn--primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('addItemModal.saving') : t('actions.save')}
              </button>
            </div>

          </form>
        )}

      </div>
    </div>
  );
};

export default ModalEditItem;
